/**
 * Analytics utility functions for tracking user interactions and device information
 */

/**
 * Get device type (Android, iOS, Desktop, etc.)
 * @returns {string} Device type
 */
export const getDeviceType = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (/android/.test(userAgent)) {
    return 'Android';
  } else if (/iphone|ipad|ipod/.test(userAgent)) {
    return 'iOS';
  } else if (/windows/.test(userAgent)) {
    return 'Windows';
  } else if (/macintosh|mac os x/.test(userAgent)) {
    return 'macOS';
  } else if (/linux/.test(userAgent)) {
    return 'Linux';
  } else {
    return 'Unknown';
  }
};

/**
 * Get browser type
 * @returns {string} Browser type
 */
export const getBrowserType = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
    return 'Chrome';
  } else if (userAgent.includes('firefox')) {
    return 'Firefox';
  } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
    return 'Safari';
  } else if (userAgent.includes('edg')) {
    return 'Edge';
  } else if (userAgent.includes('opera') || userAgent.includes('opr')) {
    return 'Opera';
  } else {
    return 'Unknown';
  }
};

/**
 * Get screen resolution
 * @returns {string} Screen resolution
 */
export const getScreenResolution = () => {
  return `${window.screen.width}x${window.screen.height}`;
};

/**
 * Get timezone
 * @returns {string} Timezone
 */
export const getTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Get language
 * @returns {string} Language
 */
export const getLanguage = () => {
  return navigator.language || navigator.userLanguage;
};

/**
 * Get current location (IP-based, requires external service)
 * @returns {Promise<Object>} Location data
 */
export const getLocation = async () => {
  try {
    // Using ipapi.co for IP-based location (free tier)
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    
    return {
      country: data.country_name || 'Unknown',
      country_code: data.country_code || 'Unknown',
      region: data.region || 'Unknown',
      city: data.city || 'Unknown',
      latitude: data.latitude || null,
      longitude: data.longitude || null,
      ip: data.ip || 'Unknown'
    };
  } catch (error) {
    console.error('Failed to get location:', error);
    return {
      country: 'Unknown',
      country_code: 'Unknown',
      region: 'Unknown',
      city: 'Unknown',
      latitude: null,
      longitude: null,
      ip: 'Unknown'
    };
  }
};

/**
 * Get all analytics data
 * @returns {Promise<Object>} Complete analytics data
 */
export const getAnalyticsData = async () => {
  const [locationData] = await Promise.all([
    getLocation(),
    // Add other async operations here if needed
  ]);

  return {
    device_type: getDeviceType(),
    browser_type: getBrowserType(),
    screen_resolution: getScreenResolution(),
    timezone: getTimezone(),
    language: getLanguage(),
    user_agent: navigator.userAgent,
    ...locationData,
    timestamp: new Date().toISOString()
  };
};

/**
 * Calculate time duration between two timestamps
 * @param {string} startTime - Start timestamp (ISO string)
 * @param {string} endTime - End timestamp (ISO string)
 * @returns {Object} Duration breakdown
 */
export const calculateDuration = (startTime, endTime) => {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const diffMs = end - start;
  
  if (diffMs < 0) {
    return {
      total_seconds: 0,
      total_minutes: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      formatted: '0s'
    };
  }

  const totalSeconds = Math.floor(diffMs / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const seconds = totalSeconds % 60;

  let formatted = '';
  if (hours > 0) formatted += `${hours}h `;
  if (minutes > 0) formatted += `${minutes}m `;
  if (seconds > 0 || formatted === '') formatted += `${seconds}s`;

  return {
    total_seconds: totalSeconds,
    total_minutes: totalMinutes,
    hours,
    minutes,
    seconds,
    formatted: formatted.trim()
  };
};
